/**
 * @file RetryableErrorBoundary.jsx
 * @description Enhanced error boundary with retry capabilities and exponential backoff
 * @workItem task_037e513952f5496990749979e565725e
 */

import React from 'react';
import { tracer } from '../utils/telemetry/simpleTracer';
import { FallbackComponent } from './FallbackComponent';

/**
 * Enhanced error boundary with automatic retry and recovery capabilities
 */
export class RetryableErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false
    };
    
    this.maxRetries = props.maxRetries || 3;
    this.retryDelay = props.retryDelay || 1000;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error with telemetry
    tracer.recordError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.name || 'RetryableErrorBoundary',
      retryCount: this.state.retryCount,
      url: window.location.href
    });

    this.setState({
      error,
      errorInfo
    });

    // Call parent error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo, this.state.retryCount);
    }
  }

  handleRetry = async () => {
    const { retryCount } = this.state;
    
    if (retryCount >= this.maxRetries) {
      this.setState({ 
        error: new Error('Maximum retry attempts reached'),
        isRetrying: false 
      });
      return;
    }

    this.setState({ isRetrying: true });

    // Exponential backoff
    const delay = this.retryDelay * Math.pow(2, retryCount);
    await new Promise(resolve => setTimeout(resolve, delay));

    // Track retry attempt
    const span = tracer.startSpan('error.retry', {
      'retry.boundary': this.props.name || 'RetryableErrorBoundary',
      'retry.attempt': retryCount + 1,
      'retry.delay': delay
    });

    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1,
      isRetrying: false
    }));

    span.end();

    // Call custom retry handler if provided
    if (this.props.onRetry) {
      this.props.onRetry(retryCount + 1);
    }
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    const { hasError, error, errorInfo, retryCount, isRetrying } = this.state;
    const { children, fallback: CustomFallback } = this.props;

    if (hasError) {
      const isMaxRetriesReached = retryCount >= this.maxRetries;
      
      // Use custom fallback if provided
      if (CustomFallback) {
        return (
          <CustomFallback
            error={error}
            errorInfo={errorInfo}
            retry={isMaxRetriesReached ? undefined : this.handleRetry}
            reset={this.handleReset}
            retryCount={retryCount}
            maxRetries={this.maxRetries}
            isRetrying={isRetrying}
          />
        );
      }

      // Default fallback UI
      return (
        <FallbackComponent
          error={error}
          errorInfo={errorInfo}
          retry={isMaxRetriesReached ? undefined : this.handleRetry}
          reset={this.handleReset}
          retryCount={retryCount}
          maxRetries={this.maxRetries}
          isRetrying={isRetrying}
          componentName={this.props.name}
        />
      );
    }

    return children;
  }
}

/**
 * Hook to programmatically trigger error boundary
 */
export function useErrorHandler() {
  const [, setError] = React.useState();
  
  return React.useCallback((error) => {
    setError(() => {
      throw error;
    });
  }, []);
}

/**
 * HOC to wrap components with retryable error boundary
 */
export function withRetryableErrorBoundary(Component, options = {}) {
  return function WithRetryableErrorBoundaryComponent(props) {
    return (
      <RetryableErrorBoundary {...options}>
        <Component {...props} />
      </RetryableErrorBoundary>
    );
  };
}

export default RetryableErrorBoundary;